/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo, useState } from 'react';
import { InstantSearch, Configure } from 'react-instantsearch';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useDispatch, useSelector } from 'react-redux';
import {
	changeRowsPerPage,
	setOrder,
	setOrderBy,
	onOrderChange,
	setSelected,
} from '@/features/table/dealsTableSlice';
import { TableRefresh } from '@/components/table/TableRefresh';
import { useLocation, useParams } from 'react-router-dom';
import { usePipelineContext } from '@/context/pipelineContext';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { DEAL_STATUS_TYPES } from '@/utils/constants';
import { dealSearch } from '@/utils/typesense/clients/dealSearch';
import { parseHeadCells } from '../InstantSearchDataGrid/data';
import InstantSearchDataGrid from '../InstantSearchDataGrid';
import { paths } from '@/app/routes';
import DebtorsTableFilters from './DebtorsTableFilters';
import { useInstantSearchOwnerFilters } from '@/hooks/useInstantSearchOwnerFilters';
import { DealBulkActionsToolbar } from '@/components/table/DealBulkActionsToolbar';
import { getClientIdFilterFromId } from '@/features/clients/helper';
import { useGetClientRowQuery } from '@/features/clients/clientsApi';

export const debtorHeadCells = [
	{ id: 'dealName', label: 'Debtor name', sortable: true },
	{ id: 'clientName', label: 'Client name', sortable: true },
	{ id: 'contactName', label: 'Contact name', sortable: true },
	{ id: 'ownerDocId', label: 'Assignee', sortable: true },
	{ id: 'invoices', label: 'Associated invoices', sortable: false },
	{ id: 'effectiveDate', label: 'Outstanding', sortable: true },
	{ id: 'nextTaskDate', label: 'Next task date', sortable: true },
	{ id: 'balance', label: 'Balance', sortable: false },
	{
		id: 'automationEnabled',
		label: 'Reminders',
		sortable: true,
		sortingOrder: ['asc', 'desc'],
		hideable: false,
		reorderable: false,
	},
	{ id: 'createDate', label: 'Created date', sortable: true },
	{ id: 'dealStage', label: 'Status', sortable: true },
];

export const useDebtorFilters = () => {
	const [outStandingDaysRange, setOutStandingDaysRange] = useState(null);
	const [outStandingDaysRangeValue, setOutStandingDaysRangeValue] = useState(null);

	const { stages } = usePipelineContext(process.env.REACT_APP_PIPELINE_DEBTORS);

	const outstandingDaysSchema = useMemo(() => {
		if (outStandingDaysRange) {
			const [startTs, endTs] = outStandingDaysRange;

			if (!startTs && !endTs) {
				return '';
			}

			if (!startTs) {
				return `effectiveDate:<=${endTs}`;
			}

			if (!endTs) {
				return `effectiveDate:<=${startTs}`;
			}

			return `effectiveDate:[${startTs}..${endTs}]`;
		}
		return '';
	}, [stages, outStandingDaysRange]);

	return {
		outStandingDaysRange,
		setOutStandingDaysRange,
		outStandingDaysRangeValue,
		setOutStandingDaysRangeValue,
		outstandingDaysSchema,
	};
};

const DebtorsTable = ({ tab, displayAllAssignees = false }) => {
	const dispatch = useDispatch();

	const { clientId: urlClientId } = useParams();
	const isClientView = !!urlClientId;
	// The URL clientId may be a legacy hubspotId, so filter by the resolved row's id.
	const { data: clientRow } = useGetClientRowQuery(urlClientId, { skip: !urlClientId });
	const clientId = clientRow?.clientId ?? urlClientId;

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const { getFeatureFlags } = useFeatureFlagContext();
	const automatedDebtors = Boolean(getFeatureFlags('automatedDebtors')?.active);
	const brokerGroupIds = userDetails?.brokerGroupArray ?? [];

	const headCells = useMemo(() => {
		return automatedDebtors
			? debtorHeadCells.slice()
			: debtorHeadCells.filter((cell) => cell.id !== 'automationEnabled');
	}, [automatedDebtors]);

	const { pathname: slug } = useLocation();

	const { rowsPerPage, orders } = useSelector((state) => state.dealTable);

	const orderParams = useMemo(
		() => orders[slug] ?? { order: 'desc', orderBy: 'createdate' },
		[orders, slug],
	);
	const { order, orderBy } = orderParams;
	const { ownerFilter: ownerFilterString } = useInstantSearchOwnerFilters();

	const {
		outStandingDaysRange,
		setOutStandingDaysRange,
		outStandingDaysRangeValue,
		setOutStandingDaysRangeValue,
		outstandingDaysSchema,
	} = useDebtorFilters();

	useEffect(() => {
		dispatch(setSelected([]));
	}, [dispatch, tab]);

	useEffect(() => {
		dispatch(
			setOrderBy({
				slug,
				orderBy: orderBy ?? 'createDate',
			}),
		);
		dispatch(
			setOrder({
				slug,
				order: order ?? 'asc',
			}),
		);
	}, [slug]);

	useEffect(() => {
		dispatch(onOrderChange());
	}, [orderBy, order]);

	const { stages } = usePipelineContext(process.env.REACT_APP_PIPELINE_DEBTORS);

	const handleChangeRowsPerPage = (event) => {
		dispatch(changeRowsPerPage(event.target.value));
	};

	const filterSchema = useMemo(() => {
		const awaitingPaymentStages = `dealStage: [${stages
			.filter((s) => !s.closed && !s.completed && s.order === 0)
			.map((s) => `'${s.stageId}'`)
			.join(',')}]`;

		const premiumFundedStages = `dealStage: [${stages
			.filter((s) => !s.completed && s.order > 0)
			.map((s) => `'${s.stageId}'`)
			.join(',')}]`;

		const completedStages = `dealStage: [${stages
			.filter((s) => s.completed)
			.map((s) => `'${s.stageId}'`)
			.join(',')}]`;

		const hideArchived = `status:!=${DEAL_STATUS_TYPES.archived}`;
		const showArchived = `status:=${DEAL_STATUS_TYPES.archived}`;

		const ownerFilter = displayAllAssignees
			? `brokerGroupId: [${brokerGroupIds.join(',')}]`
			: ownerFilterString;

		const tabStageFilterMap = {
			['awaiting-payment']: [awaitingPaymentStages, hideArchived],
			['premium-funded']: [premiumFundedStages, hideArchived],
			completed: [completedStages, hideArchived],
			['invoice-report']: [],
			archived: [showArchived],
		};

		const dealStages = tabStageFilterMap[tab] ?? [];
		const schemaFilters = [
			`pipeline:=${process.env.REACT_APP_PIPELINE_DEBTORS}`,
			isClientView ? getClientIdFilterFromId(clientId) : '',
			...dealStages,
			ownerFilter,
			outstandingDaysSchema,
		].filter(Boolean);

		const schema = {
			filters: schemaFilters.join(' && '),
		};

		return schema;
	}, [
		tab,
		ownerFilterString,
		stages,
		outstandingDaysSchema,
		brokerGroupIds,
		clientId,
		isClientView,
	]);

	const columns = useMemo(() => parseHeadCells(headCells), [headCells]);

	return (
		<InstantSearch indexName='deals' searchClient={dealSearch}>
			<Configure {...filterSchema} />
			<TableRefresh table='dealTable' />
			<DebtorsTableFilters
				{...{
					outStandingDaysRange,
					setOutStandingDaysRange,
					outStandingDaysRangeValue,
					setOutStandingDaysRangeValue,
				}}
			/>
			<DealBulkActionsToolbar pipeline={process.env.REACT_APP_PIPELINE_DEBTORS} />
			<InstantSearchDataGrid
				rowsPerPage={rowsPerPage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				objectType={'deal'}
				includeTasks={true}
				columns={columns}
				includePolicies={true}
				includeTransactions={true}
				tab={tab}
				variant={paths.debtors}
				checkboxSelection={true}
				enableColumnReorder
			/>
		</InstantSearch>
	);
};

DebtorsTable.displayName = 'DebtorsTable';

export default DebtorsTable;
